import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Platform } from 'react-native';
import type { ReferralEventDto } from '../../storage/WellCheckStorage';
import DatePickerModal from '../DatePickerModal';

const STATUS_OPTIONS = ['Pending', 'Scheduled', 'In Progress', 'Completed', 'Cancelled'] as const;
type ReferralStatus = typeof STATUS_OPTIONS[number];

const STATUS_COLORS: Record<ReferralStatus, { bg: string; text: string; border: string }> = {
  'Pending':     { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  'Scheduled':   { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  'In Progress': { bg: '#f3e8ff', text: '#6b21a8', border: '#d8b4fe' },
  'Completed':   { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  'Cancelled':   { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
};

const HISTORY_STATUSES: ReferralStatus[] = ['Completed', 'Cancelled'];

const EMPTY: ReferralEventDto = {
  id: '',
  to: '',
  for: '',
  date: '',
  contactInfo: '',
  outcome: '',
  Track: 'Track',
  status: 'Pending',
  trackAction: true,
};

const makeReferralId = () => `ref_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

const normaliseReferralEvent = (event: ReferralEventDto): ReferralEventDto => ({
  ...event,
  id: (event.id || '').trim() || makeReferralId(),
  Track: ((event.Track ?? event.track ?? '').trim() !== '')
    ? (event.Track || event.track || 'Track')
    : (event.trackAction === false ? '' : 'Track'),
  trackAction: ((event.Track ?? event.track ?? '').trim() !== '') ? true : event.trackAction !== false,
});

interface Props {
  events: ReferralEventDto[];
  onUpdate: (events: ReferralEventDto[]) => void;
}

export interface ReferralListEditorHandle {
  openAdd: () => void;
}

export const ReferralListEditor = forwardRef<ReferralListEditorHandle, Props>(
  ({ events, onUpdate }, ref) => {
  const [formVisible, setFormVisible] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState<ReferralEventDto>(EMPTY);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [removeConfirmIdx, setRemoveConfirmIdx] = useState<number | null>(null);

  useImperativeHandle(ref, () => ({ openAdd }));

  const openAdd = () => {
    setDraft({ ...EMPTY, date: new Date().toISOString().slice(0, 10) });
    setEditingIdx(null);
    setFormVisible(true);
  };

  const openEdit = (idx: number) => {
    setDraft({ ...normaliseReferralEvent(events[idx]) });
    setEditingIdx(idx);
    setFormVisible(true);
  };

  const cancel = () => {
    setFormVisible(false);
    setEditingIdx(null);
    setDatePickerVisible(false);
  };

  const formatDateDisplay = (value: string): string => {
    if (!value) return 'Select date';
    const parts = value.split('-');
    if (parts.length !== 3) return value;
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!year || !month || !day) return value;
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const openDatePicker = () => setDatePickerVisible(true);

  const save = () => {
    if (!draft.to.trim() && !draft.for.trim()) return;
    const next = [...events];
    if (editingIdx !== null) next[editingIdx] = normaliseReferralEvent(draft);
    else next.push(normaliseReferralEvent(draft));
    onUpdate(next);
    setFormVisible(false);
    setEditingIdx(null);
  };

  const remove = (idx: number) => {
    onUpdate(events.filter((_, i) => i !== idx));
    setRemoveConfirmIdx(null);
  };

  const toggleTrackAction = (idx: number) => {
    const next = [...events];
    const current = normaliseReferralEvent(next[idx]);
    const nextTracked = !(current.trackAction !== false);
    next[idx] = {
      ...current,
      trackAction: nextTracked,
      Track: nextTracked ? (current.Track || current.track || 'Track') : '',
    };
    onUpdate(next);
  };

  const renderForm = (
    isEdit: boolean,
    inlineInCard: boolean = false,
  ) => {
    return (
    <View style={[styles.form, inlineInCard && styles.inlineForm]}>
      <Text style={styles.formTitle}>
        {isEdit ? '✏️ Edit Referral' : '➕ Add Referral'}
      </Text>

      {/* Referred To */}
      <Text style={styles.fieldLabel}>Referred To (Specialist / Clinic) *</Text>
      <TextInput
        style={styles.input}
        value={draft.to}
        onChangeText={v => setDraft(d => ({ ...d, to: v }))}
        placeholder="e.g. Dr. Smith - Pediatric Cardiology"
        placeholderTextColor="#aaa"
      />

      {/* Referred For */}
      <Text style={styles.fieldLabel}>Referred For (Reason) *</Text>
      <TextInput
        style={styles.input}
        value={draft.for}
        onChangeText={v => setDraft(d => ({ ...d, for: v }))}
        placeholder="e.g. Cardiac evaluation"
        placeholderTextColor="#aaa"
      />

      {/* Date Referred */}
      <Text style={styles.fieldLabel}>Date Referred</Text>
      {Platform.OS === 'web' ? (
        // @ts-ignore — web-only input
        <input
          type="date"
          value={draft.date}
          onChange={(e: any) => setDraft(d => ({ ...d, date: e.target.value }))}
          style={{
            width: 160, border: '1px solid #d0d7de', borderRadius: 6,
            padding: '6px 10px', fontSize: 13, color: '#2c3e50',
            backgroundColor: '#fafbfc', outline: 'none', marginBottom: 8,
          }}
        />
      ) : (
        <TouchableOpacity style={styles.dateButton} onPress={openDatePicker} activeOpacity={0.75}>
          <Text style={styles.dateButtonText}>{formatDateDisplay(draft.date)}</Text>
        </TouchableOpacity>
      )}

      {/* Contact Info */}
      <Text style={styles.fieldLabel}>Contact Info</Text>
      <TextInput
        style={styles.input}
        value={draft.contactInfo}
        onChangeText={v => setDraft(d => ({ ...d, contactInfo: v }))}
        placeholder="Phone / fax / email"
        placeholderTextColor="#aaa"
      />

      {/* Status chips */}
      <Text style={styles.fieldLabel}>Status</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {STATUS_OPTIONS.map(s => {
          const sel = (draft.status || 'Pending') === s;
          const sc = STATUS_COLORS[s];
          return (
            <TouchableOpacity
              key={s}
              style={{
                paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, borderWidth: 1,
                borderColor: sel ? sc.border : '#d0d7de',
                backgroundColor: sel ? sc.bg : '#fafbfc',
              }}
              onPress={() => setDraft(d => ({ ...d, status: s }))}
            >
              <Text style={{ fontSize: 12, color: sel ? sc.text : '#5a6472', fontWeight: sel ? '600' : '400' }}>
                {s}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Notes / Outcome */}
      <Text style={styles.fieldLabel}>Notes / Outcome</Text>
      <TextInput
        style={[styles.input, { minHeight: 60 }]}
        value={draft.outcome}
        onChangeText={v => setDraft(d => ({ ...d, outcome: v }))}
        placeholder="Appointment date, outcome, follow-up notes..."
        placeholderTextColor="#aaa"
        multiline
      />

      {/* Save / Cancel buttons */}
      <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
        <TouchableOpacity style={styles.saveBtn} onPress={save}>
          <Text style={styles.saveBtnText}>Save</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.cancelBtn} onPress={cancel}>
          <Text style={styles.cancelBtnText}>Cancel</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
  };

  const active    = events.map((p, idx) => ({ p, idx })).filter(({ p }) => !HISTORY_STATUSES.includes((p.status || 'Pending') as ReferralStatus));
  const completed = events.map((p, idx) => ({ p, idx })).filter(({ p }) =>  HISTORY_STATUSES.includes((p.status || 'Pending') as ReferralStatus));

  const renderRow = (p: ReferralEventDto, idx: number, isHistory: boolean) => {
    const status = (p.status || 'Pending') as ReferralStatus;
    const sc = STATUS_COLORS[status] ?? STATUS_COLORS['Pending'];
    return (
      <View
        key={idx}
        style={[
          styles.refRow,
          isHistory && { opacity: 0.75, backgroundColor: '#f9fafb' },
          formVisible && editingIdx === idx && styles.refRowEditing,
        ]}
      >
        <View style={styles.refRowTop}>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
              {p.to ? (
                <Text style={[styles.refTo, isHistory && { color: '#6b7280' }]}>{p.to}</Text>
              ) : null}
              <View style={{ backgroundColor: sc.bg, borderRadius: 8, paddingHorizontal: 6, paddingVertical: 1, borderWidth: 1, borderColor: sc.border }}>
                <Text style={{ fontSize: 10, color: sc.text, fontWeight: '600' }}>{status}</Text>
              </View>
            </View>
            {p.for ? (
              <Text style={styles.refDetail}>Referred For: {p.for}</Text>
            ) : null}
            <Text style={styles.refDetail}>
              {[
                p.date        ? `Referred: ${p.date}` : null,
                p.contactInfo ? `Contact: ${p.contactInfo}` : null,
              ].filter(Boolean).join('  ·  ')}
            </Text>
            {p.outcome ? (
              <Text style={[styles.refDetail, { fontStyle: 'italic', color: '#7f8c8d' }]}>{p.outcome}</Text>
            ) : null}
          </View>
          <View style={styles.refControlsColumn}>
            <TouchableOpacity onPress={() => toggleTrackAction(idx)} style={styles.trackActionToggle}>
              <Text style={[styles.trackActionToggleText, p.trackAction === false && styles.trackActionToggleTextOff]}>
                {p.trackAction === false ? '☐ Track in Action Plan' : '☑ Track in Action Plan'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => ((formVisible && editingIdx === idx) ? cancel() : openEdit(idx))}
              style={styles.refActionBtn}
            >
              <Text style={styles.refActionText}>{formVisible && editingIdx === idx ? 'Cancel' : 'Edit'}</Text>
            </TouchableOpacity>
            {!isHistory && (
              removeConfirmIdx === idx ? (
                <View style={{ flexDirection: 'row', gap: 4 }}>
                  <TouchableOpacity onPress={() => remove(idx)} style={styles.refActionBtn}>
                    <Text style={[styles.refActionText, { color: '#e74c3c', fontWeight: '700' }]}>Confirm?</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setRemoveConfirmIdx(null)} style={styles.refActionBtn}>
                    <Text style={styles.refActionText}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity onPress={() => setRemoveConfirmIdx(idx)} style={styles.refActionBtn}>
                  <Text style={[styles.refActionText, { color: '#e74c3c' }]}>Remove</Text>
                </TouchableOpacity>
              )
            )}
          </View>
        </View>
        {formVisible && editingIdx === idx && (
          <View style={styles.inlineEditSection}>
            {renderForm(true, true)}
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <DatePickerModal
        visible={datePickerVisible}
        title="Select Date Referred"
        initialValue={draft.date}
        accentColor="#3b82f6"
        onCancel={() => setDatePickerVisible(false)}
        onConfirm={(dateValue) => {
          setDraft((current) => ({ ...current, date: dateValue }));
          setDatePickerVisible(false);
        }}
      />

      {/* Active referrals */}
      {active.length === 0 ? (
        <Text style={styles.emptyText}>No active referrals on record.</Text>
      ) : (
        active.map(({ p, idx }) => renderRow(p, idx, false))
      )}

      {/* Completed / Cancelled history */}
      {completed.length > 0 && (
        <>
          <TouchableOpacity
            style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8, marginBottom: 4 }}
            onPress={() => setShowHistory(v => !v)}
          >
            <Text style={{ fontSize: 12, color: '#6b7280', fontWeight: '600' }}>
              {showHistory ? '▾' : '▸'} Completed / Cancelled ({completed.length})
            </Text>
          </TouchableOpacity>
          {showHistory && completed.map(({ p, idx }) => renderRow(p, idx, true))}
        </>
      )}

      {/* Add button — removed; triggered externally via ref */}

      {/* Inline add form at card bottom */}
      {formVisible && editingIdx === null && renderForm(false)}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  emptyText: {
    fontSize: 13,
    color: '#95a5a6',
    fontStyle: 'italic',
    marginBottom: 8,
  },
  refRow: {
    flexDirection: 'column',
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#fff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e8ecef',
    marginBottom: 6,
  },
  refRowTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  refTo: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2c3e50',
  },
  refDetail: {
    fontSize: 11,
    color: '#7f8c8d',
    marginTop: 1,
  },
  refControlsColumn: {
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    minWidth: 190,
  },
  trackActionToggle: {
    marginBottom: 6,
    alignSelf: 'stretch',
  },
  trackActionToggleText: {
    fontSize: 13,
    color: '#1e40af',
    fontWeight: '600',
    textAlign: 'right',
  },
  trackActionToggleTextOff: {
    color: '#6b7280',
  },
  refActionBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  refActionText: {
    fontSize: 12,
    color: '#3498db',
    fontWeight: '500',
  },
  addBtn: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#3b82f6',
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
    marginTop: 4,
  },
  addBtnText: {
    fontSize: 13,
    color: '#3b82f6',
    fontWeight: '600',
  },
  form: {
    marginTop: 8,
    padding: 12,
    backgroundColor: '#eff6ff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#93c5fd',
  },
  inlineEditSection: {
    marginTop: 8,
    paddingTop: 8,
  },
  inlineForm: {
    marginTop: 0,
    backgroundColor: '#f7faff',
  },
  refRowEditing: {
    backgroundColor: '#eff6ff',
    borderColor: '#93c5fd',
  },
  formTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#1e3a5f',
    marginBottom: 10,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  input: {
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 7,
    fontSize: 13,
    color: '#2c3e50',
    backgroundColor: '#fff',
    marginBottom: 8,
  },
  dateButton: {
    width: 160,
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 7,
    backgroundColor: '#fff',
    marginBottom: 8,
  },
  dateButtonText: {
    fontSize: 13,
    color: '#2c3e50',
    textAlign: 'center',
  },
  saveBtn: {
    flex: 1,
    backgroundColor: '#3b82f6',
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
  },
  saveBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  cancelBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  cancelBtnText: {
    color: '#5a6472',
    fontSize: 13,
    fontWeight: '500',
  },
});
